<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { debounce } from '@chatwoot/utils';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CreateTicketModal from './CreateTicketModal.vue';

const emit = defineEmits(['selectTicket']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const showCreateModal = ref(false);
const searchQuery = ref('');

const tickets = useMapGetter('supportTickets/getSupportTickets');
const meta = useMapGetter('supportTickets/getSupportTicketsMeta');
const filters = useMapGetter('supportTickets/getSupportTicketFilters');
const isLoading = useMapGetter('supportTickets/getSupportTicketListLoading');
const statusFilterOptions = computed(() => [
  { value: 'open', label: t('SUPPORT_TICKETS.STATUS_OPTIONS.OPEN') },
  {
    value: 'in_progress',
    label: t('SUPPORT_TICKETS.STATUS_OPTIONS.IN_PROGRESS'),
  },
  { value: 'resolved', label: t('SUPPORT_TICKETS.STATUS_OPTIONS.RESOLVED') },
  { value: 'closed', label: t('SUPPORT_TICKETS.STATUS_OPTIONS.CLOSED') },
]);

const priorityFilterOptions = computed(() => [
  { value: 'low', label: t('SUPPORT_TICKETS.PRIORITY_OPTIONS.LOW') },
  { value: 'medium', label: t('SUPPORT_TICKETS.PRIORITY_OPTIONS.MEDIUM') },
  { value: 'high', label: t('SUPPORT_TICKETS.PRIORITY_OPTIONS.HIGH') },
  { value: 'urgent', label: t('SUPPORT_TICKETS.PRIORITY_OPTIONS.URGENT') },
]);

const commentsFilterOptions = computed(() => [
  {
    value: 'true',
    label: t('SUPPORT_TICKETS.FILTERS.HAS_COMMENTS'),
  },
  {
    value: 'false',
    label: t('SUPPORT_TICKETS.FILTERS.NO_COMMENTS'),
  },
]);

// const perPageOptions = [
//   { value: 15, label: '15' },
//   { value: 25, label: '25' },
//   { value: 50, label: '50' },
// ];

// const totalPages = computed(() => {
//   if (!meta.value.count) return 0;
//   return Math.ceil(meta.value.count / meta.value.perPage);
// });

const priorityLabel = priority => {
  const map = {
    low: t('SUPPORT_TICKETS.PRIORITY_OPTIONS.LOW'),
    medium: t('SUPPORT_TICKETS.PRIORITY_OPTIONS.MEDIUM'),
    high: t('SUPPORT_TICKETS.PRIORITY_OPTIONS.HIGH'),
    urgent: t('SUPPORT_TICKETS.PRIORITY_OPTIONS.URGENT'),
  };
  return map[priority] || priority;
};

const statusLabel = status => {
  const map = {
    open: t('SUPPORT_TICKETS.STATUS_OPTIONS.OPEN'),
    in_progress: t('SUPPORT_TICKETS.STATUS_OPTIONS.IN_PROGRESS'),
    resolved: t('SUPPORT_TICKETS.STATUS_OPTIONS.RESOLVED'),
    closed: t('SUPPORT_TICKETS.STATUS_OPTIONS.CLOSED'),
  };
  return map[status] || status;
};

const formatDate = timestamp => {
  if (!timestamp) return '—';
  return new Date(timestamp * 1000).toLocaleDateString();
};

const conversationLabel = ticket => {
  const conversation = ticket.meta?.conversation;
  if (!conversation) return '—';
  const name = conversation.contactName || '';
  return `#${conversation.displayId}${name ? ` · ${name}` : ''}`;
};

const applyFilters = partial => {
  store.dispatch('supportTickets/setListFilters', partial);
};

const statusFilterValue = computed(() =>
  filters.value.status === 'all' ? '' : filters.value.status
);
const priorityFilterValue = computed(() =>
  filters.value.priority === 'all' ? '' : filters.value.priority
);
const commentsFilterValue = computed(() =>
  filters.value.hasComments === 'all' ? '' : filters.value.hasComments
);
const commentsFilterLabel = computed(() => {
  const selected = commentsFilterOptions.value.find(
    option => option.value === commentsFilterValue.value
  );
  return selected?.label || t('SUPPORT_TICKETS.FILTERS.COMMENTS');
});

const activeFilterCount = computed(() => {
  let count = 0;
  if (filters.value.status !== 'all') count += 1;
  if (filters.value.priority !== 'all') count += 1;
  if (filters.value.hasComments !== 'all') count += 1;
  if (filters.value.dueDate) count += 1;
  return count;
});

const hasActiveFilters = computed(() => activeFilterCount.value > 0);

const debouncedSearch = debounce(value => {
  applyFilters({ q: value });
}, 300);

watch(searchQuery, value => {
  debouncedSearch(value);
});

const onStatusFilter = value => applyFilters({ status: value || 'all' });
const onPriorityFilter = value => applyFilters({ priority: value || 'all' });
const onCommentsFilter = value => applyFilters({ hasComments: value || 'all' });
const onDueDateFilter = event => {
  applyFilters({ dueDate: event.target.value });
};

const clearAllFilters = () => {
  applyFilters({
    status: 'all',
    priority: 'all',
    hasComments: 'all',
    dueDate: '',
  });
};

const onPageChange = page => {
  store.dispatch('supportTickets/setListPage', page);
};

// const onPerPageChange = event => {
//   store.dispatch('supportTickets/setListPerPage', Number(event.target.value));
// };

const openConversation = ticket => {
  const conversation = ticket.meta?.conversation;
  if (!conversation?.displayId) return;
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: conversation.displayId,
    },
  });
};

const onTicketCreated = ticket => {
  showCreateModal.value = false;
  emit('selectTicket', ticket);
};

onMounted(async () => {
  searchQuery.value = filters.value.q || '';
  await store.dispatch('supportTickets/fetchSupportTickets');
});
</script>

<template>
  <div
    class="flex flex-col flex-1 w-full h-full min-w-0 overflow-hidden bg-n-surface-1"
  >
    <header
      class="flex items-start justify-between gap-4 px-6 pt-6 pb-4 shrink-0"
    >
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('SUPPORT_TICKETS.PAGE_TITLE') }}
        </h1>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('SUPPORT_TICKETS.SUBTITLE') }}
        </p>
      </div>
      <NextButton
        icon="i-lucide-plus"
        sm
        :label="t('SUPPORT_TICKETS.NEW_TICKET')"
        @click="showCreateModal = true"
      />
    </header>

    <div
      class="flex flex-wrap items-center gap-2 px-6 shrink-0 py-3 px-6 max-md:px-4"
    >
      <div class="relative flex flex-1 min-w-[200px] max-w-sm">
        <span
          class="absolute flex items-center text-n-slate-10 left-2.5 inset-y-0 m-auto size-3.5 pointer-events-none"
        >
          <span class="i-lucide-search size-4" />
        </span>
        <input
          v-model="searchQuery"
          type="search"
          class="!pl-8 !pr-8 !h-9 !text-sm !m-0"
          :placeholder="t('SUPPORT_TICKETS.SEARCH_PLACEHOLDER')"
        />
      </div>

      <!-- <FilterSelect
        v-if="showPageSizeSelector"
        v-model="currentPageSize"
        variant="outline"
        hide-icon
        class="[&>button]:text-n-slate-11 [&>button]:hover:text-n-slate-12 [&>button]:h-6"
        :options="pageSizeOptions"
      /> -->

      <FilterSelect
        :model-value="statusFilterValue"
        :label="statusFilterValue ? null : t('SUPPORT_TICKETS.FILTERS.STATUS')"
        :options="statusFilterOptions"
        class="[&>button]:text-n-slate-11 [&>button]:hover:text-n-slate-12 [&>button]:h-9"
        @update:model-value="onStatusFilter"
      />
      <FilterSelect
        :model-value="priorityFilterValue"
        :label="
          priorityFilterValue ? null : t('SUPPORT_TICKETS.FILTERS.PRIORITY')
        "
        :options="priorityFilterOptions"
        class="[&>button]:text-n-slate-11 [&>button]:hover:text-n-slate-12 [&>button]:h-9"
        @update:model-value="onPriorityFilter"
      />
      <FilterSelect
        :model-value="commentsFilterValue"
        :label="t('SUPPORT_TICKETS.FILTERS.COMMENTS')"
        :options="commentsFilterOptions"
        hide-icon
        class="[&>button]:text-n-slate-11 [&>button]:hover:text-n-slate-12 [&>button]:h-9"
        @update:model-value="onCommentsFilter"
      >
        <template #trigger="{ toggle }">
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-100 ease-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-8 px-3 text-sm active:enabled:scale-[0.97] justify-center"
            @click="toggle"
          >
            <span class="i-lucide-message-circle size-4 text-n-slate-10" />
            {{ commentsFilterLabel }}
            <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
          </button>
        </template>
      </FilterSelect>

      <div class="relative min-w-[10rem]">
        <!-- <span
          class="absolute inset-y-0 flex items-center text-n-slate-10 right-3"
        >
          <span class="i-lucide-calendar size-4" />
        </span> -->
        <input
          :value="filters.dueDate"
          type="date"
          class="!m-0 !h-9 !text-n-slate-11"
          @change="onDueDateFilter"
        />
      </div>

      <button
        v-if="hasActiveFilters"
        type="button"
        class="inline-flex items-center text-sm text-n-slate-11 hover:text-n-ruby-11 gap-1 h-9 px-2.5 rounded-md font-medium hover:bg-n-ruby-6/20 transition-colors ml-auto"
        @click="clearAllFilters"
      >
        <span class="i-lucide-x size-3.5" />
        {{
          t('SUPPORT_TICKETS.FILTERS.CLEAR', {
            count: activeFilterCount,
          })
        }}
      </button>
    </div>

    <div class="flex flex-col flex-1 min-h-0 px-6 pb-0">
      <div
        class="flex flex-col flex-1 min-h-0 overflow-hidden border rounded-xl border-n-weak"
      >
        <div class="flex-1 overflow-auto">
          <table class="w-full min-w-[64rem]">
            <thead class="sticky top-0 z-[1] bg-n-surface-2">
              <tr class="border-b border-n-weak">
                <th
                  v-for="header in [
                    'TABLE.ID',
                    'TABLE.SUBJECT',
                    'TABLE.STATUS',
                    'TABLE.PRIORITY',
                    'TABLE.CONVERSATION',
                    'TABLE.COMMENTS',
                    'TABLE.DUE_DATE',
                    'TABLE.CREATED',
                  ]"
                  :key="header"
                  class="px-4 py-3 text-xs font-medium tracking-wide text-left uppercase text-n-slate-10"
                >
                  {{ t(`SUPPORT_TICKETS.${header}`) }}
                </th>
              </tr>
            </thead>
            <tbody v-if="isLoading">
              <tr>
                <td colspan="8" class="py-16 text-center">
                  <Spinner />
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="!tickets.length">
              <tr>
                <td
                  colspan="8"
                  class="py-16 text-sm text-center text-n-slate-11"
                >
                  {{ t('SUPPORT_TICKETS.NO_RESULTS') }}
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr
                v-for="ticket in tickets"
                :key="ticket.id"
                class="border-b cursor-pointer border-n-weak hover:bg-n-alpha-1 last:border-b-0"
                @click="emit('selectTicket', ticket)"
              >
                <td class="px-4 py-3 text-sm text-n-slate-12">
                  #{{ ticket.displayId }}
                </td>
                <td class="px-4 py-3 text-sm text-n-slate-12 max-w-[14rem]">
                  <span class="line-clamp-1">{{ ticket.subject }}</span>
                </td>
                <td class="px-4 py-3 text-sm text-n-slate-11">
                  {{ statusLabel(ticket.status) }}
                </td>
                <td class="px-4 py-3 text-sm text-n-slate-11">
                  {{ priorityLabel(ticket.priority) }}
                </td>
                <td class="px-4 py-3 text-sm">
                  <button
                    v-if="ticket.meta?.conversation"
                    type="button"
                    class="text-n-brand hover:underline"
                    @click.stop="openConversation(ticket)"
                  >
                    {{ conversationLabel(ticket) }}
                  </button>
                  <span v-else class="text-n-slate-11">—</span>
                </td>
                <td class="px-4 py-3 text-sm text-n-slate-11">
                  {{ ticket.commentsCount || 0 }}
                </td>
                <td class="px-4 py-3 text-sm text-n-slate-11">
                  {{ formatDate(ticket.dueDate) }}
                </td>
                <td class="px-4 py-3 text-sm text-n-slate-11">
                  {{ formatDate(ticket.createdAt) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer
          class="flex items-center justify-between gap-4 px-4 py-3 border-t border-n-weak shrink-0"
        >
          <!-- <div class="flex items-center gap-2 text-sm text-n-slate-11">
            <span>{{ t('SUPPORT_TICKETS.ROWS_PER_PAGE') }}</span>
            <select
              :value="meta.perPage"
              class="px-2 py-1 text-sm border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12"
              @change="onPerPageChange"
            >
              <option
                v-for="opt in perPageOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <span>
              {{
                t('SUPPORT_TICKETS.PAGE_OF', {
                  current: meta.currentPage,
                  total: totalPages,
                })
              }}
            </span>
          </div> -->
          <PaginationFooter
            :current-page="meta.currentPage"
            :total-items="meta.count"
            :items-per-page="meta.perPage"
            class="!px-0 before:hidden bg-transparent border-0"
            @update:current-page="onPageChange"
          />
        </footer>
      </div>
    </div>

    <CreateTicketModal
      v-if="showCreateModal"
      @close="showCreateModal = false"
      @created="onTicketCreated"
    />
  </div>
</template>
